import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { getCookie } from '../cookies';
import { TypesenseUser, TypesenseSearchKeyResponse, TypesenseConfig } from './types';

export class TypesenseService {
  private typesenseAdapter: TypesenseInstantSearchAdapter | null = null;
  private searchKeyUrl: string;
  private typesenseApiUrl: string;
  private userCollection = 'team_user_info';

  constructor() {
    this.searchKeyUrl = import.meta.env.VITE_TYPESENSE_API_KEY_URL || '';
    // Use the same pattern as the original - get from environment
    this.typesenseApiUrl = import.meta.env.VITE_TYPESENSE_API || '';
  }

  private async getSearchKey(): Promise<string> {
    // Check if we have a valid cached key
    const cachedConfig = this.getCachedConfig();
    if (cachedConfig && this.isSearchKeyValid(cachedConfig)) {
      return cachedConfig.searchKey;
    }

    // Get gravity token from auth store or context
    const gravityToken = this.getGravityToken();
    if (!gravityToken) {
      throw new Error('No gravity token available');
    }

    try {
      const response = await fetch(this.searchKeyUrl, {
        method: 'GET',
        headers: {
          'gravity-token': gravityToken,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get search key: ${response.statusText}`);
      }

      const data: TypesenseSearchKeyResponse = await response.json();
      
      // Store the key in session storage for reuse
      const config: TypesenseConfig = {
        searchKey: data.searchKey,
        expiresAt: data.expiresAt,
      };
      sessionStorage.setItem('typesense_config', JSON.stringify(config));
      
      return data.searchKey;
    } catch (error) {
      console.error('Failed to get Typesense search key:', error);
      throw error;
    }
  }

  private getCachedConfig(): TypesenseConfig | null {
    try {
      const cached = sessionStorage.getItem('typesense_config');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }

  private isSearchKeyValid(config: TypesenseConfig): boolean {
    const currentTimestamp = new Date().getTime();
    return config.expiresAt > currentTimestamp;
  }

  private getGravityToken(): string | null {
    return getCookie('gravity_token');
  }

  private async initializeTypesenseInstance(): Promise<void> {
    if (this.typesenseAdapter) {
      return;
    }

    const searchKey = await this.getSearchKey();
    
    // Use the typesense-instantsearch-adapter as per the documentation
    this.typesenseAdapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: searchKey,
        nodes: [
          {
            url: this.typesenseApiUrl,
          },
        ],
        cacheSearchResultsForSeconds: 2 * 60,
      },
      additionalSearchParameters: {
        query_by: 'email,first_name,last_name',
        page: 1,
      },
    });
  }

  async searchUsers(query: string, limit: number = 25): Promise<TypesenseUser[]> {
    if (!query.trim()) {
      return [];
    }

    try {
      await this.initializeTypesenseInstance();
      
      if (!this.typesenseAdapter) {
        throw new Error('Typesense adapter not initialized');
      }

      const searchParameters = {
        q: query,
        query_by: 'email,first_name,last_name',
        per_page: Math.min(limit, 10), // Reduced from 250 to 10 for faster results
        page: 1,
        sort_by: '_text_match:desc', // Sort by relevance for faster results
      };

      // Use the typesense-instantsearch-adapter as per the documentation
      const result = await this.typesenseAdapter.typesenseClient
        .collections(this.userCollection)
        .documents()
        .search(searchParameters, { cacheSearchResultsForSeconds: 30 });

      // Map results exactly like the original
      return result.hits?.map((hit: any) => {
        const data = hit.document as TypesenseUser;
        return data;
      }) || [];
    } catch (error) {
      console.error('Typesense search failed:', error);
      return [];
    }
  }

  clearCache(): void {
    sessionStorage.removeItem('typesense_config');
    this.typesenseAdapter = null;
  }

  // Pre-initialize the Typesense instance (call when dialog opens)
  async preInitialize(): Promise<void> {
    try {
      await this.initializeTypesenseInstance();
    } catch (error) {
      console.error('Failed to pre-initialize Typesense:', error);
      // Don't throw - let the search handle the error
    }
  }
}
